import { CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import NotFound from "../../../../assets/images/not-found.svg";
import { useSegmentPalettes } from "../../../../hooks/useSegmentPalettes";
import useSegmentSettings from "../../../../hooks/useSegmentSetting";
import { openModal } from "../../../../redux/reducers/dialog";
import {
  pushToHistoryAction,
  setWheelColorPalette,
  updateSegmentColorPalette,
} from "../../../../redux/reducers/segmentBuilder";
import {
  getSelectedSegment,
  getWheelId,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  deletePalette,
  getPaletteLibrary,
  renameColorPalette,
  savePalette,
} from "../../../../services/api";
import Button from "../../../Button";
import Tabs from "../../../Tabs";
import TextView from "../../../TextView";
import LibraryItem from "./LibraryItem";
import * as Styled from "./styles";

export default function ColorLibrary({
  onClose,
  searchText,
  onBack,
  isWheelWizard,
  onPaletteChange,
  paletteType:type,
}) {
  const [savedPalettes, setSavedPalettes] = useState([]);
  const [recentPalettes, setRecentPalettes] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { updateSegmentColors } = useSegmentPalettes();

  const childTabs = ["Saved", "Recent"];
  const [selectedTab, setSelectedTab] = useState(childTabs[0]);

  const { handleChange } = useSegmentSettings();

  const dispatch = useDispatch();

  const selectedArc = useSelector(getSelectedSegment) || {};
  const wheelId = useSelector(getWheelId);

  const { settings: segmentSettings = {} } = selectedArc;
  const { color: initialSegmentColor } = segmentSettings;

  const usageType = type==="wheel" ? 1 : 2;
  const paletteType = selectedTab == "Saved" ? 1 : 2;

  const selectedPalette =
    selectedTab === "Saved"
      ? savedPalettes.find((palette) => palette.selected)
      : recentPalettes.find((palette) => palette.selected);

  useEffect(() => {
    if (onPaletteChange && selectedPalette) {
      onPaletteChange({
        ...selectedPalette,
        paletteName: selectedPalette.palette_name,
      });
    }
  }, [selectedPalette, onPaletteChange]);

  const debouncedFetchPalettes = useCallback(
    debounce(async (usageType, paletteType, query) => {
      setSavedPalettes([]);
      setRecentPalettes([]);
      setError(null);

      try {
        const response = await getPaletteLibrary(
          wheelId,
          paletteType,
          query,
          usageType
        );

        if (response.data) {
          if (paletteType === 1) {
            setSavedPalettes(response.data.results);
          } else if (paletteType === 2) {
            setRecentPalettes(response.data.results);
          }
        } else {
          setError("Failed to fetch palettes.");
        }
      } catch (err) {
        console.error("Error fetching palettes:", err);
        setError("An error occurred while fetching palettes.");
      } finally {
        setLoading(false);
      }
    }, 300),
    [wheelId]
  );

  useEffect(() => {
    setLoading(true);
    debouncedFetchPalettes(usageType, paletteType, searchText);
  }, [selectedTab, searchText, debouncedFetchPalettes]);

  const handleDeletePalette = async (data, type) => {
    try {
      const response = await deletePalette(data?.palette?.id);

      if (response.data.success) {
        // Notify success
        toast.success("Palette deleted successfully!");
        if (type === "1") {
          setSavedPalettes((prev) =>
            prev.filter((palette) => palette.id !== data.palette.id)
          );
        } else if (type === "2") {
          setRecentPalettes((prev) =>
            prev.filter((palette) => palette.id !== data.palette.id)
          );
        }
      } else {
        toast.error("Failed to delete the palette. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting palette:", error);
      toast.error("An error occurred while deleting the palette.");
    }
  };

  const handleSavePalette = async (data) => {
    const { palette_name, id } = data.palette;
    try {
      const response = await savePalette({
        id: typeof id === 'string' ? undefined : id,
        palette_name: palette_name,
        wheel_id: wheelId,
        ...data.palette,
        type: 1,
      });

      if (response.data.success) {
        toast.success("Palette saved successfully!");
      } else {
        toast.error(response.data.message);
        setTimeout(() => setError(""), 10000);
      }
    } catch (error) {
      console.error("Error saving palette:", error);
    }
  };

  const handleOnClickPalette = (palette, type) => {
    if (type == "1") {
      setSavedPalettes((prev) =>
        prev.map((item) => ({
          ...item,
          selected: item.id === palette.id,
        }))
      );
    } else {
      setRecentPalettes((prev) =>
        prev.map((item) => ({
          ...item,
          selected: item.id === palette.id,
        }))
      );
    }
  };

  const handleConfirm = () => {
    if (type==="wheel" && !isWheelWizard) {
      dispatch(
        openModal({
          onConfirm: handleSelectPalette,
          title: "Change Wheel Palette",
          subtitle:
            "Changing the Wheel Palette will update the color palettes for each Segment as well. This can take several seconds, but will occur in the background as you continue to build. Still Proceed?",
        })
      );
    } else {
      if (selectedPalette.colors[0]?.hex !== initialSegmentColor)
        dispatch(
          openModal({
            onConfirm: handleSelectPalette,
            title: "Change Segment Color",
            subtitle:
              "This Palette will change the Segment's Color. Proceed anyway?",
          })
        );
      else handleSelectPalette();
    }
  };

  const handleSelectPalette = async () => {
    try {
      if (!selectedPalette) {
        toast.error("Please select a palette before proceeding.");
        return;
      }

      dispatch(pushToHistoryAction());
      if (type==="wheel") {
        dispatch(
          setWheelColorPalette({
            paletteData: selectedPalette,
            paletteId: selectedPalette.id,
          })
        );
        updateSegmentColors(selectedPalette);
      } else {
        if (!selectedArc.id)
          return toast.error("Please open a segment before proceeding.");
        handleChange("segmentColor", null, selectedPalette.colors[0]?.hex);
        dispatch(
          updateSegmentColorPalette({
            palette: selectedPalette,
            paletteId: selectedPalette.id,
            id: selectedArc?.id,
          })
        );
      }

      onClose();
    } catch (error) {
      console.error("Error saving palette:", error);
      toast.error("An error occurred while selecting the palette.");
    }
  };

  const handleRenamePalette = async (data) => {
    const { newTitle, paletteId, paletteType, type } = data;
    
    try {
      // Find the palette to get its current data
      const currentPalettes = type === 1 ? savedPalettes : recentPalettes;
      const paletteToUpdate = currentPalettes.find(palette => palette.id === paletteId);
      
      if (!paletteToUpdate) {
        toast.error("Palette not found.");
        return;
      }

      const response = await renameColorPalette({
        id: paletteId,
        palette_name: newTitle,
      });

      if (response.data?.success) {
        // Update local state arrays
        if (type === 1) {
          setSavedPalettes((prev) =>
            prev.map((palette) =>
              palette.id === paletteId
                ? { ...palette, palette_name: newTitle }
                : palette
            )
          );
        } else if (type === 2) {
          setRecentPalettes((prev) =>
            prev.map((palette) =>
              palette.id === paletteId
                ? { ...palette, palette_name: newTitle }
                : palette
            )
          );
        }

        toast.success("Palette renamed successfully!");
      } else {
        toast.error(response.data.message || "Failed to rename the palette.");
      }
    } catch (error) {
      console.error("Error renaming palette:", error);
      toast.error("An error occurred while renaming the palette.");
    }
  }

  const palettes = selectedTab === "Saved" ? savedPalettes : recentPalettes;

  return (
    <>
      <Styled.ModalContent style={{ height: "50vh", overflowY: "hidden" }}>

          <div style={{ maxHeight: "46vh", overflowY: "scroll",height:"100%" }}>
            <Tabs
              categories={childTabs}
              selectedCategory={selectedTab}
              onCategoryChange={(_, val) => setSelectedTab(val)}
              tabStyle={{ maxWidth: 100 }}
            />

            {loading ? (
              <Styled.LoaderWrapper>
                <CircularProgress />
              </Styled.LoaderWrapper>
            ) : palettes?.length ? (
              <Styled.GridContainer
                style={{
                  gridTemplateColumns:
                    type==="wheel" ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
                }}
              >
                {palettes?.map((item) => (
                  <LibraryItem
                    palette={item}
                    selectedMenuId={selectedMenuId}
                    setSelectedMenuId={setSelectedMenuId}
                    paletteType={type}
                    type={paletteType}
                    handleDeletePalette={handleDeletePalette}
                    handleOnClickPalette={handleOnClickPalette}
                    handleSavePalette={
                      selectedTab == "Recent" && handleSavePalette
                    }
                    handleRenamePalette={handleRenamePalette}
                  />
                ))}
              </Styled.GridContainer>
            ) : (
              <>
                <Styled.CenteredContainer>
                  <img
                    src={NotFound}
                    alt="Not Found"
                    width={100}
                    height={100}
                  />
                  <TextView variant="title">
                    {searchText.length
                      ? "No Records found!"
                      : selectedTab == "Saved"
                      ? " You haven't saved any palettes yet!"
                      : "You haven't used any palette yet!"}
                  </TextView>
                  {!searchText.length && (
                    <Button style={{ width: 200 }} onClick={onBack}>
                      {selectedTab == "Saved"
                        ? "Start saving!"
                        : "Start using!"}
                    </Button>
                  )}
                </Styled.CenteredContainer>
              </>
            )}
          </div>
      </Styled.ModalContent>
      {!isWheelWizard && (
        <Styled.ModalFooter>
          <Styled.ButtonsContainer>
            <Button
              variant={"secondary"}
              onClick={isWheelWizard ? onBack : onClose}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Select</Button>
          </Styled.ButtonsContainer>
        </Styled.ModalFooter>
      )}
    </>
  );
}
